import { rankPlayers, rankSwiss, type PlayerRow, type SwissRow } from "@rally/draw";
import type { Game } from "@rally/rating";

/**
 * The table, from the results that exist.
 *
 * Derived rather than stored. A standings row is a sum of matches, and a sum kept alongside
 * the thing it sums is a sum that goes wrong: void a match and a stored table is a lie until
 * somebody rebuilds it. Recomputing a dozen fixtures on the way to the screen costs nothing.
 *
 * The tiebreak chain is requirements §5.3's, in its order: **matches won → head-to-head →
 * game difference → point difference**. Every step is reported back so the screen can say
 * which one separated two people, because the one thing a club argues about is the order of
 * a table and "the app said so" has never settled an argument.
 */

export interface StandingsFixture {
  pool?: number | null;
  playersA?: string[] | null;
  playersB?: string[] | null;
  result?: { games: Game[]; winner: "A" | "B" } | null;
}

export interface StandingRow {
  /** Whatever `keyOf` returned: a pair, a player, a side of a tie. */
  key: string;
  memberIds: string[];
  played: number;
  won: number;
  lost: number;
  gamesFor: number;
  gamesAgainst: number;
  pointsFor: number;
  pointsAgainst: number;
  /**
   * Which rule put this row above the one below it. Null for the last row, and for a row
   * separated by nothing at all — two competitors can be genuinely level on all four.
   */
  separatedBy: Tiebreak | null;
}

export type Tiebreak = "won" | "head-to-head" | "games" | "points";

export const TIEBREAK_LABEL: Record<Tiebreak, string> = {
  won: "matches won",
  "head-to-head": "head to head",
  games: "game difference",
  points: "point difference",
};

export interface StandingsGroup {
  /** Null when the event is one flat group rather than pools. */
  pool: number | null;
  rows: StandingRow[];
}

/**
 * @param keyOf what counts as one competitor. Singles is the player; doubles is the pair,
 * which is why this is a function and not a member id — the same two people are one row for
 * the length of a round robin. A team tie passes the side, and every fixture of the tie
 * collapses onto two rows.
 */
export function buildStandings(
  fixtures: StandingsFixture[],
  keyOf: (memberIds: string[]) => string | null = defaultKey,
): StandingsGroup[] {
  const groups = new Map<number | null, Map<string, StandingRow>>();

  for (const fixture of fixtures) {
    if (!fixture.result || !fixture.playersA?.length || !fixture.playersB?.length) continue;

    const keyA = keyOf(fixture.playersA);
    const keyB = keyOf(fixture.playersB);
    // A competitor the caller cannot place — somebody removed from a team sheet, say. Their
    // opponent's record would be half a match, which is worse than leaving it out.
    if (keyA === null || keyB === null || keyA === keyB) continue;

    const pool = fixture.pool ?? null;
    const table = groups.get(pool) ?? new Map<string, StandingRow>();
    groups.set(pool, table);

    const a = row(table, keyA, fixture.playersA);
    const b = row(table, keyB, fixture.playersB);
    const tally = countGames(fixture.result.games);

    a.played += 1;
    b.played += 1;
    a.gamesFor += tally.gamesA;
    a.gamesAgainst += tally.gamesB;
    b.gamesFor += tally.gamesB;
    b.gamesAgainst += tally.gamesA;
    a.pointsFor += tally.pointsA;
    a.pointsAgainst += tally.pointsB;
    b.pointsFor += tally.pointsB;
    b.pointsAgainst += tally.pointsA;

    if (fixture.result.winner === "A") {
      a.won += 1;
      b.lost += 1;
    } else {
      b.won += 1;
      a.lost += 1;
    }
  }

  return [...groups.entries()]
    .sort(([x], [y]) => (x ?? 0) - (y ?? 0))
    .map(([pool, table]) => ({
      pool,
      rows: order([...table.values()], fixtures, keyOf),
    }));
}

/** The pair, or the player. Sorted, so the two orders of the same pair are one competitor. */
const defaultKey = (memberIds: string[]) => [...memberIds].sort().join("+");

export type IndividualRow = PlayerRow;

/**
 * The other kind of table: **one row per player, points accumulated** (requirements §5.5).
 *
 * An americano rotates partners every round, so a pair is not a thing that persists and
 * ranking pairs would rank an accident. What carries across the evening is you: every point
 * you were on court for is yours, whoever you were playing with.
 *
 * The ranking itself moved into `@rally/draw`, because Mexicano *draws from* it — 1st with
 * 4th against 2nd with 3rd, straight down this order — and the generator runs in an Edge
 * Function where this file does not exist. A second copy there would be a table that could
 * disagree with the fixtures generated from it, which is the one argument nobody in a hall
 * could be talked out of. This stays as the name the screens already call it by.
 */
export const buildIndividualStandings = (fixtures: StandingsFixture[]): IndividualRow[] =>
  rankPlayers(fixtures);

function row(table: Map<string, StandingRow>, key: string, memberIds: string[]): StandingRow {
  const existing = table.get(key);
  if (existing) return existing;

  const fresh: StandingRow = {
    key,
    memberIds,
    played: 0,
    won: 0,
    lost: 0,
    gamesFor: 0,
    gamesAgainst: 0,
    pointsFor: 0,
    pointsAgainst: 0,
    separatedBy: null,
  };
  table.set(key, fresh);
  return fresh;
}

function countGames(games: Game[]) {
  let gamesA = 0;
  let gamesB = 0;
  let pointsA = 0;
  let pointsB = 0;

  for (const game of games) {
    pointsA += game.a;
    pointsB += game.b;
    if (game.a > game.b) gamesA += 1;
    else gamesB += 1;
  }

  return { gamesA, gamesB, pointsA, pointsB };
}

/**
 * Sort, then record what did the sorting.
 *
 * Head-to-head is a mini-league over the tied block rather than a single result, because
 * three people on four wins each is the ordinary case in a round robin and "who beat whom"
 * has no answer between three of them until you count. With a block of two it reduces to
 * exactly the single result everybody expects.
 */
function order(
  rows: StandingRow[],
  fixtures: StandingsFixture[],
  keyOf: (memberIds: string[]) => string | null,
): StandingRow[] {
  const sorted = [...rows].sort((x, y) => compare(x, y, rows, fixtures, keyOf) ?? 0);

  for (const [index, entry] of sorted.entries()) {
    const next = sorted[index + 1];
    entry.separatedBy = next ? (rule(entry, next, sorted, fixtures, keyOf) ?? null) : null;
  }

  return sorted;
}

const diff = (row: StandingRow, of: "games" | "points") =>
  of === "games" ? row.gamesFor - row.gamesAgainst : row.pointsFor - row.pointsAgainst;

function compare(
  x: StandingRow,
  y: StandingRow,
  all: StandingRow[],
  fixtures: StandingsFixture[],
  keyOf: (memberIds: string[]) => string | null,
): number {
  if (x.won !== y.won) return y.won - x.won;

  const tied = all.filter((row) => row.won === x.won);
  if (tied.length > 1) {
    const mine = headToHead(x, tied, fixtures, keyOf);
    const theirs = headToHead(y, tied, fixtures, keyOf);
    if (mine !== theirs) return theirs - mine;
  }

  if (diff(x, "games") !== diff(y, "games")) return diff(y, "games") - diff(x, "games");
  if (diff(x, "points") !== diff(y, "points")) return diff(y, "points") - diff(x, "points");
  return 0;
}

/** Which rule separated these two, in the same order `compare` applies them. */
function rule(
  x: StandingRow,
  y: StandingRow,
  all: StandingRow[],
  fixtures: StandingsFixture[],
  keyOf: (memberIds: string[]) => string | null,
): Tiebreak | null {
  if (x.won !== y.won) return "won";

  const tied = all.filter((row) => row.won === x.won);
  if (
    tied.length > 1 &&
    headToHead(x, tied, fixtures, keyOf) !== headToHead(y, tied, fixtures, keyOf)
  ) {
    return "head-to-head";
  }

  if (diff(x, "games") !== diff(y, "games")) return "games";
  if (diff(x, "points") !== diff(y, "points")) return "points";
  return null;
}

/** Wins counted only against the others on the same number of wins. */
function headToHead(
  entry: StandingRow,
  tied: StandingRow[],
  fixtures: StandingsFixture[],
  keyOf: (memberIds: string[]) => string | null,
): number {
  const among = new Set(tied.map((row) => row.key));
  let wins = 0;

  for (const fixture of fixtures) {
    if (!fixture.result || !fixture.playersA?.length || !fixture.playersB?.length) continue;
    const keyA = keyOf(fixture.playersA);
    const keyB = keyOf(fixture.playersB);
    if (keyA === null || keyB === null) continue;
    if (!among.has(keyA) || !among.has(keyB)) continue;

    const winner = fixture.result.winner === "A" ? keyA : keyB;
    if (winner === entry.key) wins += 1;
  }

  return wins;
}

/**
 * The Swiss table: wins, then Buchholz, then game and point difference — `rankSwiss` from
 * `@rally/draw`, the same function the next round is paired from, so the table on the wall and
 * the round on court cannot disagree. A bye is a fixture against nobody and counts as a win.
 */
export function buildSwissStandings(
  fixtures: (StandingsFixture & { sideB: { kind: string } })[],
): SwissRow[] {
  const byes = fixtures
    .filter((fixture) => fixture.sideB.kind === "bye")
    .map((fixture) => fixture.playersA?.[0])
    .filter((memberId): memberId is string => Boolean(memberId));
  return rankSwiss(fixtures, byes);
}
